/**
 * MyLibraryPage — Michael's dedicated home for every installation
 * a user has created. Mar 6 2026.
 *
 * The /my-installations page is the CHOOSE-and-CREATE surface
 * (Create with Soulumo / Build Your Own / Signature Installations).
 * This page is the KEEP surface — nothing else on it, just their
 * personal library, freshly loaded, favourites first, newest first.
 */
import { PracticeCard } from "../../components/soulumoPractice/PracticeCard";
import { PracticeOffer } from "../../components/soulumoPractice/PracticeOffer";
import { queueLabel } from "../../lib/queueLabel";
import { TrialKeep } from "../../components/soulumoCreate/ReadyStep";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { Loader2, Plus, ChevronLeft, Moon } from "lucide-react";
import SoulumoWord from "../../components/SoulumoWord";
import InstallOrb from "../../components/InstallOrb";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";
import WellbeingAckBanner from "../../components/WellbeingAckBanner";
import { VoiceCloneEntry } from "../../components/voiceClone/VoiceCloneEntry";
import { isBrandedNarrow, resolveBrand } from "../../lib/brand";
import InlineRecorder from "./InlineRecorder";
import { useListenDays } from "../../hooks/useListenDays";
import { NightStartOverlay, NightStartToggle, nightStartDue } from "../../components/soulumoCreate/NightStart";
import { GiftSheet } from "../../components/soulumoCreate/GiftSheet";
import { GiftThanksInbox } from "../../components/soulumoCreate/GiftThanks";
import { InstallationActions, DeleteInstallButton } from "../../components/soulumoCreate/InstallationActions";
import { RevoiceButton, RetryButton, SendGiftButton } from "../../components/soulumoCreate/RevoiceButton";
import { Gift as GiftIcon } from "lucide-react";
import { BulkSelectList, RecentlyDeleted, Pill } from "../../components/soulumoCreate/LibraryBulk";
import { SharedWithMe } from "../../components/atelier/ShareDream";

const LISTEN_GOAL = 21;
const celebratedKey = (id) => `soulumo_21_celebrated_${id}`;

function Day21Moment({ j, onClose }) {
  const nav = useNavigate();
  const close = () => { localStorage.setItem(celebratedKey(j.id), "1"); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-5" onClick={(e) => e.stopPropagation()}
      style={{ background: "rgba(6,10,20,0.82)", backdropFilter: "blur(16px)", textTransform: "none", letterSpacing: "normal", fontFamily: '"Manrope", sans-serif' }} data-testid="day21-moment">
      <div className="w-full max-w-md rounded-3xl p-8 text-center sig-rise" style={{ background: "radial-gradient(ellipse at 50% 0%, #1a3058 0%, #0d1f3a 60%, #091629 100%)", border: `1px solid ${TOK.gold}` }}>
        <div className="mx-auto w-20 h-20 rounded-full animate-pulse" style={{ background: "radial-gradient(circle, rgba(215,187,122,0.7), transparent 70%)" }} />
        <div className="mt-5 text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: TOK.goldSoft }}>21 days complete</div>
        <h2 className="mt-3 text-3xl font-light" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}>These words are becoming yours.</h2>
        <p className="mt-3 text-lg italic" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.creamDim }} data-testid="day21-name">
          You gave {j.name || "this installation"} twenty-one days. Keep it close — or explain what you&apos;d like to change next.
        </p>
        <button type="button" onClick={() => { close(); nav("/my-journeys"); }} data-testid="day21-next"
          className="mt-7 w-full rounded-full text-[0.74rem] uppercase font-bold" style={{ minHeight: 56, background: TOK.gold, color: "#0b1430", letterSpacing: "0.26em" }}>
          Create my next installation
        </button>
        <button type="button" onClick={close} data-testid="day21-keep" className="mt-4 text-sm italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
          Keep listening to this one
        </button>
      </div>
    </div>,
    document.body
  );
}
function ListenChip({ days, id }) {
  const done = days >= LISTEN_GOAL;
  return (
    <span data-testid={`library-listen-days-${id}`} className="inline-flex items-center gap-1.5 rounded-full px-2 py-[2px]"
      style={{ border: `1px solid ${days ? TOK.gold : TOK.goldMist}`, color: days ? TOK.gold : TOK.creamMist, letterSpacing: "0.16em" }}>
      <svg width="10" height="10" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${(Math.min(days, LISTEN_GOAL) / LISTEN_GOAL) * 50.3} 50.3`} transform="rotate(-90 10 10)" />
      </svg>
      {done ? "21 days complete" : days ? `Day ${days} of ${LISTEN_GOAL}` : "Begin your 21 days"}
    </span>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const TOK = {
  bg: "radial-gradient(ellipse at 30% 20%, #1f3a5c 0%, #0d1f3a 35%, #060e1f 70%, #03060f 100%)",
  ink: "#0d1f3a",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.45)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  goldGlow: "rgba(231,201,138,0.10)",
};

const fmtSec = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

// ────────────────────────────────────────────────────────────────────
// LibraryCategoryNav — Oct 2026
// Sticky three-chip bar at the top of /my-library so testers can jump
// straight to Installations · Recorded · Lines-to-Record without
// scrolling past hundreds of items. Highlights whichever section is
// currently in view (IntersectionObserver). Horizontally scrolls on
// narrow phones; chips wrap on wider viewports.
// ────────────────────────────────────────────────────────────────────
function LibraryCategoryNav({ counts }) {
  const [active, setActive] = React.useState("library-installations");

  React.useEffect(() => {
    const ids = ["library-installations", "library-recorded", "library-pending"];
    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;

    // Fire whenever a section's top crosses ~1/3 down the viewport.
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  const chips = [
    { id: "library-installations", label: "Installations",         count: counts.installations },
    { id: "library-recorded",      label: "Recorded Words", count: counts.recorded },
    { id: "library-pending",       label: "Words to Record", count: counts.pending },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid="library-category-nav"
      className="sticky z-20 -mx-5 sm:-mx-8 px-5 sm:px-8 py-3 mb-6 backdrop-blur-md"
      style={{
        top: 0,
        background: "linear-gradient(180deg, rgba(6,14,31,0.92) 0%, rgba(6,14,31,0.78) 100%)",
        borderBottom: `1px solid ${TOK.goldMist}`,
      }}
    >
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {chips.map((c) => {
          const isActive = active === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => scrollTo(c.id)}
              data-testid={`library-category-chip-${c.id.replace("library-", "")}`}
              className="flex-shrink-0 inline-flex items-center gap-2 rounded-full transition"
              style={{
                padding: "8px 14px",
                background: isActive ? "rgba(231,201,138,0.18)" : "rgba(245,224,173,0.04)",
                border: `1px solid ${isActive ? TOK.gold : TOK.goldMist}`,
                color: isActive ? TOK.cream : TOK.creamDim,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                minHeight: 36,
              }}
            >
              <span>{c.label}</span>
              <span
                className="rounded-full px-1.5 py-0.5"
                style={{
                  background: isActive ? TOK.gold : "rgba(231,201,138,0.1)",
                  color: isActive ? TOK.ink : TOK.goldSoft,
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  minWidth: 22,
                  textAlign: "center",
                  lineHeight: 1,
                }}
              >
                {c.count}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ────────────────────────────────────────────────────────────────────
// VoiceCloneInterestCard — Oct 2026
// Premium add-on demand gauge. Testers tap "Count me in" → short
// modal with optional note → persists an interest row via
// /api/voice-clone/interest. Switches to a "You're on the list"
// state once submitted so we never nag the same user twice. No
// hard commitment, no payment — Michael flips the switch when he
// sees enough signal to justify building the generator.
// ────────────────────────────────────────────────────────────────────
function VoiceCloneInterestCard() {
  const [state, setState] = React.useState({ loading: true, signedUp: false });
  const [showForm, setShowForm] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("reclaim_token");
        const r = await fetch(`${API}/api/voice-clone/interest/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = r.ok ? await r.json() : { signed_up: false };
        setState({ loading: false, signedUp: !!d.signed_up });
      } catch {
        setState({ loading: false, signedUp: false });
      }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setSubmitting(true);
    try {
      const token = localStorage.getItem("reclaim_token");
      const r = await fetch(`${API}/api/voice-clone/interest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ note: note.trim(), source: "library" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState({ loading: false, signedUp: true });
      setShowForm(false);
      setNote("");
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return null;

  if (state.signedUp) {
    return (
      <div
        data-testid="voice-clone-signedup"
        className="mb-8 rounded-2xl px-5 py-4 flex items-start gap-3"
        style={{
          background: "rgba(224,169,122,0.06)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.creamDim,
        }}
      >
        <span
          className="flex-shrink-0 rounded-full w-6 h-6 flex items-center justify-center text-xs"
          style={{ background: TOK.gold, color: TOK.ink, fontWeight: 600 }}
          aria-hidden
        >
          ✓
        </span>
        <div style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontSize: "0.95rem", lineHeight: 1.5 }}>
          You&apos;re on the list for <strong>My voice</strong>. Michael
          will reach out personally when it&apos;s ready. Thank you for the
          signal.
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="voice-clone-card"
        className="relative mb-8 rounded-2xl p-5 sm:p-6 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(224,169,122,0.14) 0%, rgba(224,169,122,0.04) 60%, rgba(10,20,40,0.4) 100%)",
          border: `1px solid ${TOK.gold}`,
          boxShadow: "0 18px 60px rgba(0,0,0,0.35), 0 0 36px rgba(224,169,122,0.1)",
        }}
      >
        <div
          className="uppercase mb-2"
          style={{
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.36em",
          }}
        >
          Premium add-on · coming soon
        </div>
        <h3
          className="font-light leading-tight italic"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.5rem, 5vw, 1.9rem)",
          }}
        >
          Your own voice.
        </h3>
        <p
          className="mt-2 italic max-w-md"
          style={{
            color: TOK.creamDim,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1rem",
            lineHeight: 1.55,
          }}
        >
          Record a 90-second sample once. Then every new installation
          speaks your words in <em>your</em> voice. The same calm, in the
          voice you know best.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="voice-clone-count-me-in"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 transition"
            style={{
              background: TOK.gold,
              color: TOK.ink,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.78rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              minHeight: 40,
              fontWeight: 500,
            }}
          >
            Count me in
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = "/voice-clone"; }}
            data-testid="voice-clone-unlock-btn"
            className="rounded-full px-5 py-2 transition-transform duration-200 active:scale-[0.98]"
            style={{
              background: TOK.gold,
              color: "#0d1f3a",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.7rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              minHeight: 40,
              fontWeight: 600,
            }}
          >
            Unlock now · <s className="mr-1 opacity-60" data-testid="library-unlock-was-price">£9.99</s>£6.99 / month
          </button>
        </div>
      </div>

      {showForm && (
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-5"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
          onClick={() => setShowForm(false)}
          data-testid="voice-clone-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6"
            style={{
              background: TOK.ink,
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
              maxHeight: "92vh",
              overflowY: "auto",
            }}
          >
            <div
              className="uppercase"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.58rem",
                letterSpacing: "0.36em",
              }}
            >
              Use my own voice
            </div>
            <h3
              className="mt-1 italic font-light"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.5rem",
              }}
            >
              Count me in
            </h3>
            <p
              className="mt-2 italic"
              style={{
                color: TOK.creamDim,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.95rem",
                lineHeight: 1.55,
              }}
            >
              Anything you&apos;d want to use this for? (Optional — leave
              blank if you just want to be first in line.)
            </p>
            <form onSubmit={submit} className="mt-4 space-y-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder="e.g. For the words I say to my mum each morning…"
                data-testid="voice-clone-note"
                style={{
                  width: "100%",
                  background: "rgba(0,0,0,0.3)",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.cream,
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.92rem",
                  resize: "vertical",
                  minHeight: 96,
                  outline: "none",
                }}
              />
              {err && (
                <div className="text-sm" style={{ color: "#f0a0a0" }} data-testid="voice-clone-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="voice-clone-submit"
                className="w-full rounded-full py-3 disabled:opacity-60"
                style={{
                  background: TOK.gold,
                  color: TOK.ink,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.78rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  minHeight: 44,
                  fontWeight: 500,
                }}
              >
                {submitting ? "Sending…" : "Count me in"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                data-testid="voice-clone-cancel"
                className="w-full rounded-full py-2"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.creamMist,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.78rem",
                  letterSpacing: "0.14em",
                }}
              >
                Not right now
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default function MyLibraryPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // ?open=<jid> — when present (used after the wizard finishes
  // rendering an installation) we auto-expand that row's inline
  // player so the user lands straight in playback. Mar 6 2026.
  const autoOpenId = sp.get("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Feb 10 2026 · My Library reorganized into 3 sections. Recorded
  // = user voice clips (voice-clips endpoint). Selected = BYO
  // library lines the user has saved but not yet recorded in their
  // own voice. Both fetched alongside the main installation list.
  const [voiceClips, setVoiceClips] = useState([]);
  const [byoLines, setByoLines] = useState([]);
  const narrowBrand = isBrandedNarrow(resolveBrand());
  const [selecting, setSelecting] = useState(false);
  const [trashKey, setTrashKey] = useState(0);
  const afterBulk = () => { setSelecting(false); setTrashKey((k) => k + 1); load({ silent: true }); };

  // Background polls refresh silently so open modals / typed text survive.
  const load = useCallback(async (opts) => {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError("");
    try {
      const tok = getToken();
      const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
      const fetchAll = () => Promise.all([
        fetch(`${API}/api/audio-journeys`, { headers, cache: "no-store" }),
        fetch(`${API}/api/audio-journeys/voice-clips`, { headers, cache: "no-store" }),
        fetch(`${API}/api/audio-journeys/library/lines`, { headers, cache: "no-store" }),
      ]);
      let [rJ, rV, rL] = await fetchAll().catch(() => [{ ok: false, status: 0 }, {}, {}]);
      if (rJ.status === 401) {
        window.location.href = "/soulumo/join?mode=signin";
        return;
      }
      if (!rJ.ok) {
        // One quiet retry rides out a brief network / server blip.
        await new Promise((res) => setTimeout(res, 1500));
        [rJ, rV, rL] = await fetchAll();
      }
      if (!rJ.ok) throw new Error(`HTTP ${rJ.status}`);
      const dJ = await rJ.json();
      setItems(dJ?.items || []);
      // Recorded + BYO fetches are best-effort — they should never
      // block the primary installations render.
      try { const dV = rV.ok ? await rV.json() : { items: [] }; setVoiceClips(dV?.items || []); } catch { setVoiceClips([]); }
      try { const dL = rL.ok ? await rL.json() : { items: [] }; setByoLines(dL?.items || []); } catch { setByoLines([]); }
    } catch (e) {
      console.error("[library] load failed:", e);
      if (!silent) setError("We could not load your library. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any item is still rendering so the user sees progress.
  useEffect(() => {
    const rendering = items.some((j) => j.audio_status === "rendering");
    if (!rendering) return;
    const t = setInterval(() => load({ silent: true }), 4000);
    return () => clearInterval(t);
  }, [items, load]);

  const ready = items.filter((j) => j.audio_status === "ready");
  const rendering = items.filter((j) => j.audio_status === "rendering");
  const failed = items.filter((j) => j.audio_status === "failed");

  return (
    <div
      className="min-h-screen relative overflow-x-hidden flex flex-col"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="my-library-page"
    >
      <SoulumoHeader />
      <div className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 pt-2 pb-16 relative">
        {/* Back — subtle, still available */}
        <div className="mb-3">
          <button
            onClick={() => nav("/my-installations")}
            data-testid="library-back"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5"
            style={{
              background: "transparent",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.creamDim,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            <ChevronLeft size={11} /> Back
          </button>
        </div>

        {/* Hero */}
        <div className="text-center mb-8">
          <div style={{ width: 110, height: 110, margin: "0 auto" }}>
            <InstallOrb state="resting" size={110} />
          </div>
          <p
            className="mt-5 text-[0.6rem] uppercase tracking-[0.4em]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            · Your Library ·
          </p>
          <h1
            className="mt-3 font-light leading-tight"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(2rem, 6vw, 2.75rem)",
              color: TOK.cream,
            }}
          >
            Every installation, in one place.
          </h1>
          <p
            className="mt-3 italic mx-auto max-w-md leading-relaxed"
            style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem" }}
          >
            Your saved journeys, ready to play whenever you need them.
          </p>
        </div>

        {/* Wellbeing acknowledgement — shown once, then dismissed. */}
        <WellbeingAckBanner />

        {/* Count strip */}
        {!loading && (
          <div
            className="mb-6 flex items-center justify-between rounded-2xl px-5 py-3"
            style={{ background: "rgba(245,224,173,0.03)", border: `1px solid ${TOK.goldMist}` }}
            data-testid="library-stats"
          >
            <div className="flex items-center gap-5">
              <StatPill n={ready.length} label="Ready" />
              {rendering.length > 0 && <StatPill n={rendering.length} label="Rendering" pulse />}
              {failed.length > 0 && <StatPill n={failed.length} label="Failed" />}
            </div>
            <button
              onClick={() => nav("/my-installations")}
              data-testid="library-create-new"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5"
              style={{
                background: TOK.gold,
                color: TOK.ink,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.24em",
                textTransform: "uppercase",
              }}
            >
              <Plus size={11} /> New
            </button>
            <button onClick={() => nav("/my-installations?gift=1")} data-testid="library-make-gift"
              className="ml-2 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5"
              style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: '"Manrope", sans-serif', fontSize: "0.6rem", letterSpacing: "0.24em", textTransform: "uppercase" }}>
              <GiftIcon size={11} /> Gift
            </button>
          </div>
        )}

        {!loading && !error && <RecentlyDeleted refreshKey={trashKey} onChanged={() => load({ silent: true })} />}
        {!loading && <GiftThanksInbox gold={TOK.gold} cream={TOK.cream} />}
        {!loading && items.length > 0 && <NightStartToggle />}

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: TOK.creamMist }}>
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : error ? (
          <div
            className="rounded-2xl p-6 text-center italic"
            style={{ background: "rgba(224,169,122,0.06)", border: `1px solid ${TOK.goldMist}`, color: TOK.gold, fontFamily: '"Cormorant Garamond", serif' }}
          >
            {error}
            <div className="mt-4">
              <button
                onClick={load}
                className="rounded-full px-5 py-2"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.cream,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.62rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
              >
                Try again
              </button>
            </div>
          </div>
        ) : items.length === 0 ? (
          <>
            <EmptyState onCreate={() => nav("/my-installations")} />
            <SharedWithMe />
          </>
        ) : (
          <>
            {/* Oct 2026 · Sticky category chips. Testers can jump
             * straight to the three sections (Installations · Recorded
             * Affirmations · Lines to Record) instead of scrolling
             * past a 100+ item wall. Highlights the active section as
             * they scroll. Hidden on empty-library state. */}
            <div className="mb-6"><PracticeCard /></div>
            {(() => {
              const recordedSet = new Set(voiceClips.map((v) => `${v.section}::${v.line_text}`));
              const pendingCount = byoLines.filter((l) => !recordedSet.has(`${l.section}::${l.text}`)).length;
              return (
                <LibraryCategoryNav
                  counts={{
                    installations: items.length,
                    recorded: voiceClips.length,
                    pending: pendingCount,
                  }}
                />
              );
            })()}

            {/* Voice-clone demand-gauge card. Only shows up on
             *  Soulumo library so Reclaim users don't see it. */}
            <VoiceCloneEntry />

            {/* Section 1 — Installations */}
            <section id="library-installations" style={{ scrollMarginTop: 90 }}>
            <SectionHeader
              label="Installations"
              blurb="Every audio journey you've created."
              count={items.length}
              testId="library-section-installations"
            />
            <div className="-mt-2 mb-4 flex justify-end">
              {!selecting && <Pill onClick={() => setSelecting(true)} testId="library-edit-toggle">Edit · select many</Pill>}
            </div>
            {selecting ? (
              <div className="mb-10"><BulkSelectList items={items} onDone={afterBulk} onCancel={() => setSelecting(false)} /></div>
            ) : (
            <ul className="space-y-3 mb-10" data-testid="library-items">
              {items.map((j) => (
                <li key={j.id} data-testid={`library-item-${j.id}`}>
                  <LibraryRow j={j} onShare={() => { setTrashKey((k) => k + 1); load({ silent: true }); }} autoOpen={autoOpenId === j.id} />
                </li>
              ))}
            </ul>
            )}
            </section>
            <SharedWithMe />

            {/* Section 2 — My Recorded Affirmations */}
            <section id="library-recorded" style={{ scrollMarginTop: 90 }}>
            <SectionHeader
              label="My Recorded Words"
              blurb="Lines you've spoken in your own voice."
              count={voiceClips.length}
              testId="library-section-recorded"
            />
            {voiceClips.length === 0 ? (
              <EmptySmall
                text="Record any line in your own voice and it will land here."
                testId="library-recorded-empty"
              />
            ) : (
              <ul className="space-y-2 mb-10" data-testid="library-recorded-items">
                {voiceClips.map((v) => (
                  <li key={v.id} data-testid={`library-recorded-${v.id}`}>
                    <RecordedRow clip={v} onChanged={load} />
                  </li>
                ))}
              </ul>
            )}
            </section>

            {/* Section 3 — Affirmations in Need of Recording */}
            <section id="library-pending" style={{ scrollMarginTop: 90 }}>
            <SectionHeader
              label="Words to Record"
              blurb="Lines you've saved. Record them anytime to hear them in your voice."
              count={(function () {
                const recordedSet = new Set(voiceClips.map((v) => `${v.section}::${v.line_text}`));
                return byoLines.filter((l) => !recordedSet.has(`${l.section}::${l.text}`)).length;
              })()}
              testId="library-section-selected"
            />
            {(function () {
              const recordedSet = new Set(voiceClips.map((v) => `${v.section}::${v.line_text}`));
              const pending = byoLines.filter((l) => !recordedSet.has(`${l.section}::${l.text}`));
              if (pending.length === 0) {
                return (
                  <EmptySmall
                    text="Nothing pending — every line you've saved has been recorded in your voice."
                    testId="library-selected-empty"
                  />
                );
              }
              return (
                <ul className="space-y-2 mb-10" data-testid="library-selected-items">
                  {pending.map((l) => (
                    <li key={l.id} data-testid={`library-selected-${l.id}`}>
                      <SelectedRow line={l} onChanged={load} />
                    </li>
                  ))}
                </ul>
              );
            })()}
            </section>
          </>
        )}
      </div>
      <SoulumoFooter />
    </div>
  );
}

function StatPill({ n, label, pulse }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className={pulse ? "animate-pulse" : ""}
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.4rem",
          color: TOK.cream,
          fontWeight: 300,
        }}
      >
        {n}
      </span>
      <span
        className="uppercase tracking-[0.24em]"
        style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', fontSize: "0.58rem" }}
      >
        {label}
      </span>
    </div>
  );
}

function LibraryRow({ j, onShare, autoOpen = false }) {
  const onChanged = () => onShare();
  const [expanded, setExpanded] = useState(autoOpen);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(j.audio_duration_seconds || (j.duration_minutes || 0) * 60);
  const audioRef = React.useRef(null);
  const rowRef = React.useRef(null);
  const dur = duration || j.audio_duration_seconds || (j.duration_minutes || 0) * 60;
  const status = j.audio_status;
  const isReady = status === "ready";
  const isRendering = status === "rendering";
  const isFailed = status === "failed";
  const themeChip = (j.affirmation_theme || "").replace(/_/g, " ");
  const listenDays = useListenDays(audioRef, j.id, (j.listen_days || []).length, expanded && isReady);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (listenDays >= LISTEN_GOAL && !localStorage.getItem(celebratedKey(j.id))) setCelebrate(true);
  }, [listenDays, j.id]);

  // Scroll fresh-from-wizard rows into view when auto-opened.
  useEffect(() => {
    if (autoOpen && rowRef.current) {
      setTimeout(() => rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    }
  }, [autoOpen]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => { setPlaying(false); setProgress(1); };
    const onMeta = () => { if (a.duration) setDuration(a.duration); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("loadedmetadata", onMeta);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("loadedmetadata", onMeta);
    };
  }, [expanded]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!expanded) { setExpanded(true); return; }
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else if (nightStartDue() && a.currentTime < 1) { a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {}); setNight(true); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };
  const [night, setNight] = useState(false);

  const fmtMS = (s) => {
    s = Math.max(0, Math.round(s || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const share = async (e) => {
    e.stopPropagation();
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${j.id}/share`, {}, auth());
      setGift(`${window.location.origin}${r.data.url}`);
      onShare && onShare();
    } catch (err) {
      console.warn("[share] failed:", err);
      setGift("error");
    }
  };
  const [gift, setGift] = useState(null);

  return (
    <div
      ref={rowRef}
      className="rounded-2xl p-4 transition-all"
      style={{
        background: isReady
          ? "linear-gradient(135deg, rgba(231,201,138,0.09) 0%, rgba(231,201,138,0.03) 100%)"
          : "rgba(245,224,173,0.03)",
        border: `1px solid ${isReady ? TOK.gold : TOK.goldMist}`,
        opacity: isReady ? 1 : 0.75,
      }}
      data-testid={`library-row-${j.id}`}
    >
      <button
        onClick={toggle}
        disabled={!isReady}
        className="w-full text-left flex items-start gap-4"
        style={{ cursor: isReady ? "pointer" : "default" }}
        data-testid={`library-row-toggle-${j.id}`}
      >
        <div
          className="shrink-0 rounded-full w-12 h-12 flex items-center justify-center"
          style={{ background: TOK.goldGlow, border: `1px solid ${isReady ? TOK.gold : TOK.goldMist}` }}
        >
          {isRendering ? (
            <Loader2 size={16} color={TOK.goldSoft} className="animate-spin" />
          ) : isFailed ? (
            <span style={{ color: "#e0857a", fontSize: "1rem" }}>!</span>
          ) : playing ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill={TOK.gold}>
              <rect x="6" y="5" width="4" height="14" />
              <rect x="14" y="5" width="4" height="14" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill={TOK.gold}>
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {j.favourited && <span style={{ color: TOK.gold, fontSize: "0.85rem" }}>★</span>}
            <h3 className="text-lg leading-tight truncate" style={{ fontFamily: '"Cormorant Garamond", serif', color: TOK.cream }}>
              {j.name || "Untitled Installation"}
            </h3>
          </div>
          <div
            className="mt-1 flex items-center gap-2 flex-wrap"
            style={{ fontFamily: '"Manrope", sans-serif', fontSize: "0.58rem", color: TOK.creamMist, letterSpacing: "0.14em", textTransform: "uppercase" }}
          >
            <span>{fmtSec(dur)}</span>
            {themeChip && <span>·</span>}
            {themeChip && <span>{themeChip}</span>}
            {j.created_at && <span>·</span>}
            {j.created_at && <span>{fmtDate(j.created_at)}</span>}
            {!isRendering && !isFailed && ((j.voice_overrides_count || 0) > 0 || !!j.clone_quota) && (
              <span
                data-testid={`library-in-your-voice-${j.id}`}
                className="inline-flex items-center rounded-full px-2 py-[2px]"
                style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, letterSpacing: "0.16em" }}
              >
                In your voice
              </span>
            )}
            {isReady && <ListenChip days={listenDays} id={j.id} />}
            {j.has_night && (
              <span data-testid={`library-night-badge-${j.id}`} title="Night Version ready" aria-label="Night Version ready"
                className="inline-flex items-center gap-1 rounded-full px-2 py-[2px]" style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, letterSpacing: "0.16em" }}>
                <Moon size={11} /> Night
              </span>
            )}
            {celebrate && <Day21Moment j={j} onClose={() => setCelebrate(false)} />}
          </div>
          {isRendering && (
            <p className="mt-2 italic" style={{ color: TOK.goldSoft, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.86rem" }} data-testid={`library-queue-status-${j.id}`}>
              {queueLabel(j)}
            </p>
          )}
          {j.clone_trial_pending && j.audio_status === "ready" && (
            <div className="mt-3" data-testid={`library-trial-keep-${j.id}`}>
              <TrialKeep journey={j} onKept={() => window.location.reload()} />
            </div>
          )}
          {isFailed && (
            <p className="mt-2 italic" style={{ color: "#e0857a", fontFamily: '"Cormorant Garamond", serif', fontSize: "0.86rem" }}>
              {j.remusic_failed ? "That music change didn't work this time. Your free change has been given back, and your original is safe." : "Something went wrong. Please try creating this installation again."}
            </p>
          )}
        </div>
      </button>
      {isFailed && <div className="mt-2 flex flex-wrap gap-3"><RetryButton j={j} onChanged={onChanged} /><DeleteInstallButton j={j} onDeleted={onChanged} /></div>}

      {expanded && isReady && (
        <div className="mt-4" data-testid={`library-player-${j.id}`}>
          {/* Progress */}
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,224,173,0.08)" }}>
            <div
              className="h-full transition-all"
              style={{ width: `${Math.min(100, progress * 100)}%`, background: `linear-gradient(90deg, ${TOK.gold} 0%, #f4d59f 100%)` }}
            />
          </div>
          <div
            className="flex items-center justify-between mt-1.5 tabular-nums"
            style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif', fontSize: "0.62rem" }}
          >
            <span>{fmtMS(progress * dur)}</span>
            <span>{fmtMS(dur)}</span>
          </div>

          <audio
            ref={audioRef}
            src={`${API}/api/audio-journeys/${j.id}/audio`}
            preload="metadata"
            data-testid={`library-audio-${j.id}`}
          />
          <div className="mt-4"><PracticeOffer journeyId={j.id} heading={false} /></div>
          {gift && gift !== "error" && <GiftSheet title={j.name || j.title || "Your installation"} url={gift} journeyId={j.id} onClose={() => setGift(null)} />}
          {gift === "error" && <p className="mt-3 text-xs" style={{ color: "#e9a39c" }} data-testid="gift-error">Couldn't make the gift link just now. Please try again.</p>}
          {night && <NightStartOverlay onCancel={() => setNight(false)} onFinish={() => { setNight(false); audioRef.current?.play().then(() => setPlaying(true)).catch(() => {}); }} />}

          <div className="mt-4 flex gap-2 flex-wrap">
            <button
              onClick={share}
              data-testid={`library-share-${j.id}`}
              className="rounded-full px-4 py-2"
              style={{
                background: "transparent",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.creamDim,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.6rem",
                letterSpacing: "0.26em",
                textTransform: "uppercase",
              }}
            >
              Send to a friend
            </button>
          </div>
          <InstallationActions j={j} onChanged={onChanged} />
          <RevoiceButton j={j} onChanged={onChanged} />
          {j.gift && !j.saved_from_slug && <SendGiftButton j={j} />}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      className="rounded-3xl px-6 py-14 text-center"
      style={{
        background: "rgba(245,224,173,0.03)",
        border: `1px solid ${TOK.goldMist}`,
      }}
      data-testid="library-empty"
    >
      <p
        className="text-[0.58rem] uppercase tracking-[0.4em] mb-3"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        · Your library is quiet ·
      </p>
      <h2
        className="font-light leading-tight"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.75rem", color: TOK.cream }}
      >
        Create your first installation.
      </h2>
      <p
        className="mt-3 italic mx-auto max-w-sm leading-relaxed"
        style={{ color: TOK.creamDim, fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem" }}
      >
        Choose Create with Soulumo, Build Your Own, or pick a Signature Installation.
      </p>
      <button
        onClick={onCreate}
        data-testid="library-empty-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3"
        style={{
          background: TOK.gold,
          color: TOK.ink,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.72rem",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
        }}
      >
        <Plus size={14} /> Create an installation
      </button>
    </div>
  );
}


/* ── Section chrome ────────────────────────────────────────────────
 * Category label used for each of the three sections of My Library:
 * Installations · My Recorded Affirmations · Affirmations in Need
 * of Recording.
 *
 * Feb 27 2026 · Michael flagged that the earlier version was so
 * subtle you could scroll past it without registering. Upgraded to
 * a proper category tag: gold pill with count, serif label, warm
 * accent line and generous breathing space above/below. Every
 * section now reads as its own "chapter" of the library.
 */
function SectionHeader({ label, blurb, count, testId }) {
  return (
    <div className="mb-5 mt-8 first:mt-2" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1"
          style={{
            background: "rgba(231,201,138,0.12)",
            border: `1px solid ${TOK.gold}`,
            color: TOK.gold,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 4, height: 4, borderRadius: "50%",
              background: TOK.gold,
              boxShadow: `0 0 8px ${TOK.gold}`,
            }}
          />
          Category
        </span>
        <div className="flex-1 h-px" style={{ background: TOK.goldMist }} />
        <span
          className="text-[0.62rem] uppercase tracking-[0.28em]"
          style={{
            color: TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
          }}
        >
          {count} {count === 1 ? "item" : "items"}
        </span>
      </div>
      <h2
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(1.5rem, 5.5vw, 1.9rem)",
          fontWeight: 300,
          color: TOK.cream,
          letterSpacing: "0.005em",
          lineHeight: 1.1,
        }}
      >
        {label}
      </h2>
      {blurb && (
        <p
          className="mt-1.5 italic"
          style={{
            color: TOK.creamDim,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          {blurb}
        </p>
      )}
    </div>
  );
}

function EmptySmall({ text, testId }) {
  return (
    <div
      className="rounded-2xl px-5 py-6 mb-10 text-center italic"
      style={{
        background: "rgba(245,224,173,0.02)",
        border: `1px solid ${TOK.goldMist}`,
        color: TOK.creamMist,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "0.88rem",
      }}
      data-testid={testId}
    >
      {text}
    </div>
  );
}

/* ── Recorded affirmation row ─────────────────────────────────────
 * Voice clip with tap-to-play. Simpler than LibraryRow — no share,
 * no delete (the user manages voice clips in /voice-library). */
function RecordedRow({ clip, onChanged }) {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };
  const section = (clip.section || "").replace(/_/g, " ");
  return (
    <div
      className="flex items-center gap-3 rounded-2xl px-4 py-3"
      style={{
        background: "linear-gradient(135deg, rgba(231,201,138,0.06) 0%, rgba(231,201,138,0.02) 100%)",
        border: `1px solid ${TOK.goldMist}`,
      }}
    >
      <button
        onClick={toggle}
        data-testid={`library-recorded-play-${clip.id}`}
        aria-label={playing ? "Pause" : "Play"}
        className="shrink-0 rounded-full w-10 h-10 flex items-center justify-center"
        style={{ background: TOK.goldGlow, border: `1px solid ${TOK.gold}` }}
      >
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill={TOK.gold}>
            <rect x="6" y="5" width="4" height="14" />
            <rect x="14" y="5" width="4" height="14" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill={TOK.gold}>
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p
          className="italic leading-snug"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
          }}
        >
          {clip.line_text || clip.text || "Untitled line"}
        </p>
        {section && (
          <p
            className="mt-0.5 uppercase tracking-[0.22em]"
            style={{
              color: TOK.creamMist,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.55rem",
            }}
          >
            {section}
          </p>
        )}
      </div>
      {/* Feb 27 2026 · Michael's rebind ask — a compact Re-record
          button on every already-recorded line so users can update
          their voice at any time. */}
      <InlineRecorder
        text={clip.line_text || clip.text || ""}
        section={clip.section || ""}
        variant="rerecord"
        onDone={() => onChanged?.()}
        testIdPrefix={`library-recorded-rerecord-${clip.id}`}
      />
      <audio
        ref={audioRef}
        src={`${API}${clip.url}`}
        preload="none"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
      />
    </div>
  );
}

/* ── Selected-but-not-yet-recorded row ────────────────────────────
 * Displays a BYO library line that the user has saved but has not
 * yet recorded in their own voice. Feb 10 2026. */
function SelectedRow({ line, onChanged }) {
  const section = (line.section || "").replace(/_/g, " ");
  return (
    <div
      className="flex items-start gap-3 rounded-2xl px-4 py-3"
      style={{
        background: "rgba(245,224,173,0.02)",
        border: `1px dashed ${TOK.goldMist}`,
      }}
    >
      <div
        className="shrink-0 rounded-full w-8 h-8 flex items-center justify-center mt-0.5"
        style={{ background: "transparent", border: `1px dashed ${TOK.goldMist}`, color: TOK.goldMist }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="italic leading-snug"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
          }}
        >
          {line.text || "Untitled line"}
        </p>
        {section && (
          <p
            className="mt-0.5 uppercase tracking-[0.22em]"
            style={{
              color: TOK.creamMist,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.55rem",
            }}
          >
            {section} · in need of recording
          </p>
        )}
      </div>
      {/* Feb 27 2026 · Michael flagged that these rows only had a
          decorative mic icon and no way to actually record. This is
          the working Record button that ships the take to the
          voice-clips endpoint and moves the line into the recorded
          section on success. */}
      <InlineRecorder
        text={line.text || ""}
        section={line.section || ""}
        variant="record"
        onDone={() => onChanged?.()}
        testIdPrefix={`library-selected-record-${line.id}`}
      />
    </div>
  );
}
